import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  QueryList,
  ViewChildren
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MuiOption } from '../option/mui-option.component';
import { Subscription } from 'rxjs';

let nextUniqueId = 0;

@Component({
  selector: 'mui-radio-group',
  templateUrl: './mui-radio-group.component.html',
  styleUrls: ['./mui-radio-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MuiRadioGroup),
      multi: true
    }
  ]
})
export class MuiRadioGroup<T> implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input()
  @HostBinding('attr.id')
  get id(): string {
    return this._id;
  }

  set id(value: string) {
    this._id = value || this._uid;
  }

  @Input()
  @HostBinding('attr.aria-required')
  get required(): boolean {
    return this._required;
  }

  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
  }

  @Input()
  @HostBinding('attr.aria-disabled')
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
  }

  get vertical(): boolean {
    return this._vertical;
  }

  @Input()
  set vertical(value: boolean) {
    this._vertical = coerceBooleanProperty(value);
  }

  @Input()
  label: string | null = null;

  @HostBinding('attr.aria-labelledby')
  get labelId(): string | null {
    return this.label ? this.id + '-input' : null;
  }

  get currentValue(): T | null | undefined {
    return this._currentValue;
  }

  get name(): string | number | null {
    return this.id;
  }

  @HostBinding('class.mui-radio-group')
  _baseClass = true;

  @HostBinding('attr.role')
  readonly role = 'radiogroup';

  @ViewChildren('input', { read: ElementRef })
  radioInputs?: QueryList<ElementRef<HTMLInputElement>>;

  @ContentChildren(MuiOption)
  options?: QueryList<MuiOption<T>>;

  protected _id = '';
  protected _uid = `mui-radio-${nextUniqueId++}`;
  protected _disabled = false;
  protected _vertical = false;
  protected _currentValue: T | null | undefined = null;
  private _required = false;
  private _selectedIndex = -1;
  private _subscription?: Subscription;

  constructor(protected el: ElementRef) {
    // Ensure the setter is called once
    /* eslint-disable-next-line no-self-assign */
    this.id = this.id;
  }

  ngAfterViewInit(): void {
    this.checkForAccessibleName();

    setTimeout(() => {
      // Update the checked state of the radio buttons after the view is ready
      // See example here: https://angular.io/api/core/ViewChildren#another-example
      this.writeValue(this._currentValue);
    }, 0);
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }

  registerOnChange(fn: (_: T) => void): void {
    this._propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._propagateTouch = fn;
  }

  writeValue(value: T | null | undefined): void {
    this._currentValue = value;

    // No options or input elements available yet, e.g. on initial render
    if (!this.radioInputs || !this.options) {
      return;
    }

    // Uncheck all radio buttons
    this.radioInputs.forEach((input, i) => {
      input.nativeElement.checked = false;
    });

    const selectedIndex = this.options.toArray().findIndex((o) => o.value === value);

    if (selectedIndex < 0) {
      // No option found with the given value
      this._selectedIndex = -1;
      return;
    }

    this.selectIndex(selectedIndex, false);
  }

  onChange(value: T): void {
    this._currentValue = value;
    this._propagateChange(this._currentValue);
    this._propagateTouch();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _propagateChange: (_: T) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _propagateTouch: () => void = () => {};

  protected onKeyDown(event: KeyboardEvent, i: number): void {
    switch (event.code) {
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        this.selectPrevious();
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        this.selectNext();
        break;
      case 'Space':
        event.preventDefault();
        this.selectIndex(i);
    }
  }

  protected selectPrevious(): void {
    if (!this.isKeyboardNavigationPossible()) {
      // Prevents endless while loop when all options are disabled
      return;
    }

    let nextIndex = this.ensureIndexIsInBounds(this._selectedIndex - 1);
    let nextOption = this.options?.get(nextIndex);

    while (nextOption?.disabled) {
      nextIndex = this.ensureIndexIsInBounds(nextIndex - 1);
      nextOption = this.options?.get(nextIndex);
    }

    this.selectIndex(nextIndex);
  }

  protected selectNext(): void {
    if (!this.isKeyboardNavigationPossible()) {
      // Prevents endless while loop when all options are disabled
      return;
    }

    let nextIndex = this.ensureIndexIsInBounds(this._selectedIndex + 1);
    let nextOption = this.options?.get(nextIndex);

    while (nextOption?.disabled) {
      nextIndex = this.ensureIndexIsInBounds(nextIndex + 1);
      nextOption = this.options?.get(nextIndex);
    }

    this.selectIndex(nextIndex);
  }

  protected selectIndex(index: number, focus = true): void {
    const radio: ElementRef<HTMLInputElement> | undefined = this.radioInputs?.get(index);

    if (!radio) {
      // No input element found for the selected option
      this._selectedIndex = -1;
      return;
    }

    // Check the correct radio button
    if (radio.nativeElement.labels?.length && focus) {
      // Use the label to set focus
      radio.nativeElement.labels[0].focus();
    }

    radio.nativeElement.checked = true;
    this._selectedIndex = index;
  }

  protected hasNoSelection(): boolean {
    return this._selectedIndex === -1;
  }

  protected isKeyboardNavigationPossible(): boolean {
    if (!this.options) {
      return false;
    }

    return !this.disabled && this.options.some((o) => !o.disabled);
  }

  private ensureIndexIsInBounds(index: number): number {
    if (this.options) {
      if (index < 0) {
        return this.options.length - 1;
      }

      if (index > this.options.length - 1) {
        return 0;
      }
    }

    return index;
  }

  protected isCheckboxDisabled(option: MuiOption<T>): boolean {
    return this._disabled || option.disabled;
  }

  protected checkForAccessibleName(): void {
    if (!this.label) {
      // If no a11y check above has passed, display a console warning for developers.
      const msg =
        '[MuiRadioGroup]: Element does not have an accessible name! Set the label input of the component to make this warning disappear.';
      console.warn(msg, this.el.nativeElement);
    }
  }
}
